import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "input", "dropdown", "status",
    "addressLine1", "addressLine2", "postalCode", "city", "country"
  ]

  static values = {
    url: String,
    csrfToken: String,
    debounceMs: { type: Number, default: 500 },
    minLength: { type: Number, default: 4 }
  }

  connect() {
    this.abortController = null
    this.debounceTimer = null
    this.activeIndex = -1
    this.suggestions = []
  }

  disconnect() {
    this.clearDebounce()
    this.abortInflight()
  }

  // Actions

  onInput() {
    const query = this.inputTarget.value.trim()

    if (query.length < this.minLengthValue) {
      this.hideDropdown()
      this.clearStatus()
      return
    }

    this.clearDebounce()
    this.debounceTimer = setTimeout(() => this.performLookup(query), this.debounceMsValue)
  }

  onKeydown(event) {
    if (!this.isDropdownVisible()) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.moveActive(1)
        break
      case "ArrowUp":
        event.preventDefault()
        this.moveActive(-1)
        break
      case "Enter":
        event.preventDefault()
        if (this.activeIndex >= 0 && this.activeIndex < this.suggestions.length) {
          this.selectSuggestion(this.suggestions[this.activeIndex])
        }
        break
      case "Escape":
        this.hideDropdown()
        break
    }
  }

  onBlur() {
    // Delay to allow click on dropdown item to register
    setTimeout(() => this.hideDropdown(), 200)
  }

  selectItem(event) {
    event.preventDefault()
    const index = parseInt(event.currentTarget.dataset.index, 10)
    if (index >= 0 && index < this.suggestions.length) {
      this.selectSuggestion(this.suggestions[index])
    }
  }

  // Lookup

  async performLookup(query) {
    this.abortInflight()
    this.abortController = new AbortController()

    this.showLoading()

    const body = { query }
    const countrySelect = this.hasCountryTarget ? this.countryTarget : null
    if (countrySelect && countrySelect.value) {
      body.country_code = countrySelect.value
    }

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfTokenValue,
          "Accept": "application/json"
        },
        body: JSON.stringify(body),
        signal: this.abortController.signal
      })

      if (response.status === 429) {
        this.hideDropdown()
        this.clearStatus()
        return
      }

      if (response.status === 503) {
        this.showUnavailable()
        return
      }

      if (!response.ok) {
        this.hideDropdown()
        this.clearStatus()
        return
      }

      const data = await response.json()
      this.suggestions = data.suggestions || []

      if (this.suggestions.length === 0) {
        this.showNoResults()
      } else {
        this.renderSuggestions()
      }

      this.clearStatus()
    } catch (error) {
      if (error.name === "AbortError") return
      this.hideDropdown()
      this.clearStatus()
    }
  }

  // Selection

  selectSuggestion(suggestion) {
    const addr = suggestion.address

    if (this.hasAddressLine1Target) {
      this.addressLine1Target.value = addr.address_line_1 || ""
    }
    if (this.hasAddressLine2Target) {
      this.addressLine2Target.value = addr.address_line_2 || ""
    }
    if (this.hasPostalCodeTarget) {
      this.postalCodeTarget.value = addr.postcode || ""
    }
    if (this.hasCityTarget) {
      this.cityTarget.value = addr.city || ""
    }
    if (this.hasCountryTarget && addr.country_code) {
      const select = this.countryTarget
      const option = Array.from(select.options).find(o => o.value === addr.country_code)
      if (option) {
        select.value = addr.country_code
      }
    }

    this.hideDropdown()
    this.inputTarget.value = ""

    if (this.hasAddressLine1Target) {
      this.addressLine1Target.focus()
    }
  }

  // Rendering

  renderSuggestions() {
    this.activeIndex = -1
    const items = this.suggestions.map((suggestion, index) => {
      const id = `address-suggestion-${index}`
      return `<li>
        <button type="button"
                class="dropdown-item"
                role="option"
                id="${id}"
                data-index="${index}"
                data-action="mousedown->address-lookup#selectItem">
          ${this.escapeHtml(suggestion.label)}
        </button>
      </li>`
    }).join("")

    this.dropdownTarget.innerHTML = items
    this.showDropdown()
  }

  showNoResults() {
    this.dropdownTarget.innerHTML = `<li>
      <span class="dropdown-item-text text-muted">
        <i class="bi bi-info-circle me-1"></i>${this.escapeHtml(this.noResultsText())}
      </span>
    </li>`
    this.showDropdown()
  }

  showUnavailable() {
    this.dropdownTarget.innerHTML = `<li>
      <span class="dropdown-item-text text-muted">
        <i class="bi bi-exclamation-triangle me-1"></i>${this.escapeHtml(this.unavailableText())}
      </span>
    </li>`
    this.showDropdown()
  }

  showLoading() {
    this.statusTarget.innerHTML = `<span class="spinner-border spinner-border-sm text-muted" role="status">
      <span class="visually-hidden">Loading...</span>
    </span>`
  }

  clearStatus() {
    this.statusTarget.innerHTML = ""
  }

  // Dropdown visibility

  showDropdown() {
    this.dropdownTarget.classList.add("show")
    this.inputTarget.setAttribute("aria-expanded", "true")
  }

  hideDropdown() {
    this.dropdownTarget.classList.remove("show")
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.activeIndex = -1
  }

  isDropdownVisible() {
    return this.dropdownTarget.classList.contains("show")
  }

  // Keyboard navigation

  moveActive(direction) {
    const items = this.dropdownTarget.querySelectorAll('[role="option"]')
    if (items.length === 0) return

    // Remove active from current
    if (this.activeIndex >= 0 && this.activeIndex < items.length) {
      items[this.activeIndex].classList.remove("active")
    }

    this.activeIndex += direction
    if (this.activeIndex < 0) this.activeIndex = items.length - 1
    if (this.activeIndex >= items.length) this.activeIndex = 0

    items[this.activeIndex].classList.add("active")
    items[this.activeIndex].scrollIntoView({ block: "nearest" })
    this.inputTarget.setAttribute("aria-activedescendant", items[this.activeIndex].id)
  }

  // Utilities

  clearDebounce() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
  }

  abortInflight() {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }

  escapeHtml(text) {
    if (!text) return ""
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  noResultsText() {
    return this.element.dataset.addressLookupNoResultsText || "No addresses found"
  }

  unavailableText() {
    return this.element.dataset.addressLookupUnavailableText || "Address lookup temporarily unavailable"
  }
}
